import { useEffect, useMemo, useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { GitCommitHorizontal, History, Plus, Save, WandSparkles, Workflow } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useWorkstationPageContext } from '@/frontend/features/AgentChats/workstationPageContext'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import ProcessContextSummary from '@/frontend/features/Fde/ProcessContextSummary'
import ProcessDataCoveragePanel from '@/frontend/features/Fde/ProcessDataCoveragePanel'
import ProcessEdgeInspector from '@/frontend/features/Fde/ProcessEdgeInspector'
import ProcessFlowCanvas from '@/frontend/features/Fde/ProcessFlowCanvas'
import ProcessNodeInspector from '@/frontend/features/Fde/ProcessNodeInspector'
import { layoutProcessFlow, seedProcessFlow } from '@/frontend/features/Fde/layoutProcessFlow'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { ActionType, Evidence, FdeCopy, ModelElement, ProcessCanvasNodeDefinition, ProcessEdgeDefinition, ProcessMap, ProcessMode, ProcessNodeDefinition, Relationship, SelectedProcess } from '@/frontend/features/Fde/types'

interface Props {
  projectId: string
  processMaps: ProcessMap[]
  selectedProcess: SelectedProcess | null
  elements: ModelElement[]
  relationships: Relationship[]
  evidences: Evidence[]
  actionTypes: ActionType[]
  copy: FdeCopy
  canManage: boolean
}

export default function ProcessStudio(props: Props) {
  const [nodes, setNodes] = useState<ProcessCanvasNodeDefinition[]>(seedProcessFlow(props.selectedProcess?.definition.nodes ?? []))
  const [edges, setEdges] = useState<ProcessEdgeDefinition[]>(props.selectedProcess?.definition.edges ?? [])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)
  const [dataCoverageOpen, setDataCoverageOpen] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [layoutVersion, setLayoutVersion] = useState(0)
  const [baseRevision, setBaseRevision] = useState(props.selectedProcess?.repository_revision ?? '')
  const form = useForm({ change_note: '' })
  const processOptions = props.processMaps.filter((process) => process.element_id)
  const selectedNode = nodes.find((node) => node.id === selectedNodeId) ?? null
  const selectedEdge = edges.find((edge) => edge.id === selectedEdgeId) ?? null
  const selectedNodeCoverage = props.selectedProcess?.data_coverage.nodes.find((node) => node.node_id === selectedNodeId) ?? null

  useWorkstationPageContext('process-studio', {
    surface: 'forge',
    section: 'workspace',
    view: 'process',
    project: { id: props.projectId },
    selection: props.selectedProcess ? {
      process: {
        element_id: props.selectedProcess.summary.element_id,
        name: props.selectedProcess.summary.name,
        mode: props.selectedProcess.mode,
        repository_revision: props.selectedProcess.repository_revision,
      },
      node: selectedNode ? { id: selectedNode.id, label: selectedNode.label, kind: selectedNode.kind } : undefined,
      edge: selectedEdge ? { id: selectedEdge.id, source: selectedEdge.source, target: selectedEdge.target } : undefined,
      data_coverage_open: dataCoverageOpen,
    } : undefined,
    result: {
      nodes: nodes.length,
      edges: edges.length,
      unsaved_changes: dirty,
    },
  })

  useEffect(() => {
    if (dirty) return
    const incomingNodes = props.selectedProcess?.definition.nodes ?? []
    const incomingEdges = props.selectedProcess?.definition.edges ?? []
    let active = true
    setNodes(seedProcessFlow(incomingNodes))
    setEdges(incomingEdges)
    setBaseRevision(props.selectedProcess?.repository_revision ?? '')
    setSelectedNodeId(null)
    setSelectedEdgeId(null)
    setDataCoverageOpen(false)
    setDirty(false)
    void layoutProcessFlow(incomingNodes, incomingEdges).then((organized) => {
      if (!active) return
      setNodes(organized)
      setLayoutVersion((version) => version + 1)
    })
    return () => { active = false }
  }, [dirty, props.selectedProcess?.definition, props.selectedProcess?.repository_revision])

  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const processPath = (elementId: string, mode: ProcessMode) => fdeProjectPath(fdeRoutes.workspace, props.projectId, {
    view: 'processes', process_element_id: elementId, process_mode: mode,
  })

  function visitProcess(elementId: string, mode: ProcessMode) {
    if (dirty && !window.confirm(props.copy.workspace.process_unsaved_confirm)) return
    setDirty(false)
    router.visit(processPath(elementId, mode), {
      only: ['process_maps', 'selected_process'], preserveScroll: true, preserveState: true,
    })
  }

  function changeNodes(next: ProcessCanvasNodeDefinition[]) { setNodes(next); setDirty(true) }
  function changeEdges(next: ProcessEdgeDefinition[]) { setEdges(next); setDirty(true) }

  function addNode() {
    const node: ProcessCanvasNodeDefinition = {
      id: crypto.randomUUID(), kind: 'activity', label: props.copy.workspace.process_new_step,
      description: null, confidence: 'unknown', position: { x: 80, y: 80 + nodes.length * 112 },
      action_type_id: null, ontology_refs: [], evidence_ids: [],
    }
    changeNodes([...nodes, node])
    setSelectedNodeId(node.id)
    setSelectedEdgeId(null)
  }

  async function organize() {
    changeNodes(await layoutProcessFlow(nodes, edges))
    setLayoutVersion((version) => version + 1)
  }

  function deleteNode(nodeId: string) {
    changeNodes(nodes.filter((node) => node.id !== nodeId))
    changeEdges(edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId))
    setSelectedNodeId(null)
  }

  function save() {
    if (!props.selectedProcess) return
    const semanticNodes: ProcessNodeDefinition[] = nodes.map(({ position: _position, ...node }) => node)
    form.transform((data) => ({
      process_map: {
        mode: props.selectedProcess!.mode,
        change_note: data.change_note,
        expected_head_sha: baseRevision,
        definition: { nodes: semanticNodes, edges },
      },
    }))
    form.patch(`${fdeRoutes.processMaps}/${props.selectedProcess.summary.element_id}?project_id=${props.projectId}`, {
      preserveScroll: true,
      onSuccess: () => { setSaveOpen(false); form.reset(); setDirty(false) },
    })
  }

  if (processOptions.length === 0) return <EmptyProcesses copy={props.copy} />
  if (!props.selectedProcess) return <div className="flex h-full items-center justify-center"><Button onClick={() => visitProcess(String(processOptions[0].element_id), 'reported')}><Workflow />{props.copy.workspace.process_studio}</Button></div>

  return (
    <div className="flex h-full min-h-0 flex-col bg-background" data-testid="fde-process-studio">
      <div className="flex h-13 shrink-0 items-center gap-3 border-b border-border px-3">
        <Select value={String(props.selectedProcess.summary.element_id)} onValueChange={(value) => visitProcess(value, props.selectedProcess!.mode)}>
          <SelectTrigger className="h-8 min-w-0 max-w-72 flex-1 text-xs"><SelectValue placeholder={props.copy.workspace.process_select} /></SelectTrigger>
          <SelectContent>{processOptions.map((process) => <SelectItem key={process.element_id} value={String(process.element_id)}>{process.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={props.selectedProcess.mode} onValueChange={(value) => visitProcess(String(props.selectedProcess!.summary.element_id), value as ProcessMode)}><SelectTrigger className="h-8 w-36 shrink-0 text-xs" aria-label={props.copy.workspace.process_current_mode}><SelectValue /></SelectTrigger><SelectContent>{props.selectedProcess.mode_options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select>
        <div className="flex shrink-0 items-center gap-1">
          <VersionHistory selectedProcess={props.selectedProcess} copy={props.copy} />
          {props.canManage && <Button size="sm" variant="ghost" disabled={nodes.length === 0} onClick={organize}><WandSparkles />{props.copy.workspace.process_organize}</Button>}
          {props.canManage && <Button size="sm" variant="ghost" onClick={addNode}><Plus />{props.copy.workspace.process_add_step}</Button>}
          {props.canManage && <Button size="sm" disabled={!dirty || form.processing} onClick={() => setSaveOpen(true)}><Save />{props.copy.workspace.process_save_version}</Button>}
        </div>
      </div>
      <ProcessContextSummary processId={String(props.selectedProcess.summary.element_id)} definition={{ ...props.selectedProcess.definition, nodes, edges }} elements={props.elements} relationships={props.relationships} dataCoverage={props.selectedProcess.data_coverage} copy={props.copy} onOpenData={() => { setSelectedNodeId(null); setSelectedEdgeId(null); setDataCoverageOpen(true) }} />
      <div className="flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1 overflow-hidden">
          {nodes.length === 0 && <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-8 text-center"><div><Workflow className="mx-auto size-7 text-muted-foreground/60" /><p className="mt-3 text-sm font-medium">{props.copy.workspace.process_empty}</p><p className="mt-1 max-w-md text-xs text-muted-foreground">{props.copy.workspace.process_empty_hint}</p></div></div>}
          <ProcessFlowCanvas nodes={nodes} edges={edges} elements={props.elements} dataCoverage={props.selectedProcess.data_coverage} selectedNodeId={selectedNodeId} selectedEdgeId={selectedEdgeId} editable={props.canManage} fitViewKey={layoutVersion} copy={props.copy} onNodesChange={changeNodes} onEdgesChange={changeEdges} onSelectNode={(nodeId) => { setDataCoverageOpen(false); setSelectedNodeId(nodeId) }} onSelectEdge={setSelectedEdgeId} />
          {selectedNode && <ProcessNodeInspector projectId={props.projectId} node={selectedNode} dataCoverage={selectedNodeCoverage} elements={props.elements} evidences={props.evidences} actionTypes={props.actionTypes} copy={props.copy} onChange={(node) => changeNodes(nodes.map((candidate) => candidate.id === node.id ? node : candidate))} onDelete={() => deleteNode(selectedNode.id)} onClose={() => setSelectedNodeId(null)} />}
          {selectedEdge && <ProcessEdgeInspector edge={selectedEdge} evidences={props.evidences} copy={props.copy} onChange={(edge) => changeEdges(edges.map((candidate) => candidate.id === edge.id ? edge : candidate))} onDelete={() => { changeEdges(edges.filter((edge) => edge.id !== selectedEdge.id)); setSelectedEdgeId(null) }} onClose={() => setSelectedEdgeId(null)} />}
          {dataCoverageOpen && <ProcessDataCoveragePanel projectId={props.projectId} coverage={props.selectedProcess.data_coverage} nodes={nodes} copy={props.copy} onSelectNode={(nodeId) => { setDataCoverageOpen(false); setSelectedEdgeId(null); setSelectedNodeId(nodeId) }} onClose={() => setDataCoverageOpen(false)} />}
        </div>
      </div>
      <FdeEditorDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        title={props.copy.workspace.process_save_title}
        description={props.copy.workspace.process_save_description}
        closeLabel={props.copy.common.close}
        size="standard"
        footer={<><Button variant="outline" onClick={() => setSaveOpen(false)}>{props.copy.common.cancel}</Button><Button disabled={!form.data.change_note.trim() || form.processing} onClick={save}>{form.processing ? props.copy.workspace.process_saving : props.copy.workspace.process_save_version}</Button></>}
      >
        <label className="grid gap-2 text-sm font-medium"><span>{props.copy.workspace.process_change_note}</span><Input autoFocus value={form.data.change_note} placeholder={props.copy.workspace.process_change_note_placeholder} onChange={(event) => form.setData('change_note', event.target.value)} /></label>
      </FdeEditorDialog>
    </div>
  )
}

function EmptyProcesses({ copy }: { copy: FdeCopy }) {
  return <div className="flex h-full items-center justify-center p-8 text-center"><div><Workflow className="mx-auto size-8 text-muted-foreground/60" /><p className="mt-3 text-sm font-semibold">{copy.workspace.process_no_processes}</p><p className="mt-1 max-w-md text-xs text-muted-foreground">{copy.workspace.process_no_processes_hint}</p></div></div>
}

function VersionHistory({ selectedProcess, copy }: { selectedProcess: SelectedProcess; copy: FdeCopy }) {
  const date = useMemo(() => new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }), [])
  return <Popover><PopoverTrigger asChild><Button size="sm" variant="ghost" aria-label={copy.workspace.process_version_history}><History /><span className="tabular-nums">{selectedProcess.versions.length}</span></Button></PopoverTrigger><PopoverContent align="end" className="w-80 p-0"><div className="border-b border-border px-3 py-2.5 text-xs font-semibold">{copy.workspace.process_version_history}</div><div className="max-h-72 overflow-y-auto p-2">{selectedProcess.versions.length === 0 ? <p className="px-2 py-5 text-center text-xs text-muted-foreground">{copy.workspace.process_no_versions}</p> : selectedProcess.versions.map((version) => <div key={version.sha} className="flex gap-2 rounded-md px-2 py-2 hover:bg-muted/60"><GitCommitHorizontal className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" /><div className="min-w-0"><p className="truncate text-xs font-medium">{version.subject}</p><p className="mt-0.5 text-[10px] text-muted-foreground">{version.author_name} · {date.format(new Date(version.committed_at))}</p></div></div>)}</div></PopoverContent></Popover>
}
